/**
 * A real conversation, beside the settings that change it.
 *
 * The preview used to be a miniature of the whole app drawn out of grey
 * bars: it showed that a radius had changed, and nothing else. Almost every
 * setting on this page is about reading a chat - the type size, the gap
 * between messages, whether pictures are shown, how big an icon is drawn -
 * and none of those mean anything against a grey bar.
 *
 * So it is a conversation. Real avatars, real names in their real colours,
 * the server's own custom icons, and the same components the chat log uses,
 * so whatever a slider does to the app happens here at the same moment and
 * in the same way.
 *
 * It stays put while the settings scroll past it, which is the point of a
 * preview - one that scrolls away is a picture you have to remember.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '@/stores/session';
import { endpoints } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import { Avatar } from './Avatar';
import { EmojiText } from './EmojiText';
import { UserName, openUserCard } from './user';
import type { PublicUser } from '@/lib/types';

/**
 * A stand-in, for an account with nobody on it yet.
 *
 * Only used when there is no real friend to borrow. Names in the preview
 * open the real person's card, and a made-up id cannot open anything - so
 * the stub says so by not being pressable rather than by failing when
 * pressed.
 */
const STUB = {
  id: 'preview',
  username: 'someone',
  displayName: 'Someone',
  status: 'online',
  level: 12,
  role: 'user',
  tier: 'free'
} as unknown as PublicUser;

export function ChatPreview({ onEmojiScale }: {
  /** Lets the slider under the preview write straight back to settings. */
  onEmojiScale?: (px: number) => void;
}) {
  const me = useSession((s) => s.me);
  const settings = useSession((s) => s.settings);
  const custom = useSession((s) => s.emoji);

  /*
   * Somebody you actually know, when there is one.
   *
   * Borrowed from the friends list the app already has in hand, so the
   * preview shows their real picture and their real name colour - and
   * pressing the name opens their card, because it is their card.
   */
  const friends = useQuery({ queryKey: ['friends'], queryFn: endpoints.friends, staleTime: 60_000 });
  const other = friends.data?.friends?.[0]?.user ?? null;
  const them = other ?? STUB;
  const real = !!other;

  const scale = settings?.emojiScale ?? 34;
  const gap = settings?.messageSpacing ?? 1;
  const avatars = settings?.chatAvatars !== false;

  /*
   * One icon from the server, so the preview shows what this server
   * actually has rather than a picture of one. Without any, the line reads
   * as plain text - which is also the truth.
   */
  const icon = custom[0] ? ':' + custom[0].name + ':' : '';

  const lines = useMemo(() => ([
    { who: them, body: 'queue in five? ' + icon, mine: false },
    { who: me, body: 'yep, warming up', mine: true },
    { who: them, body: icon || '🎯', mine: false, big: true }
  ]), [me, them, icon]);

  return (
    <div className="overflow-hidden rounded-[var(--radius-l)] border border-line-soft bg-surface-0">
      {/* A head, so it reads as a conversation rather than a list */}
      <div className="flex items-center gap-2 border-b border-line-soft bg-surface-1 px-3 py-2">
        <Avatar user={them} size={22} presence />
        <span className="min-w-0 flex-1 truncate text-[12.5px] font-bold text-ink">{them.displayName || them.username}</span>
        <Icon name="phone" size={13} className="text-mute" />
        <Icon name="video" size={13} className="text-mute" />
      </div>

      <div
        className="flex flex-col px-3 py-2.5"
        style={{ gap: 4 + gap * 6 + 'px', fontSize: 'var(--chat-fs, 15px)' }}
      >
        {lines.map((line, i) => (
          <div key={i} className="flex min-w-0 items-start gap-2">
            {avatars ? (
              line.who && (line.mine || real) ? (
                <button
                  type="button"
                  onClick={(e) => line.who && openUserCard(line.who, e.currentTarget)}
                  aria-label={'Show ' + (line.who.displayName ?? 'their') + ' profile'}
                  className="mt-[2px] flex-none rounded-full outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]"
                >
                  <Avatar user={line.who} size={26} />
                </button>
              ) : line.who ? (
                <span className="mt-[2px] flex-none"><Avatar user={line.who} size={26} /></span>
              ) : <span className="h-[26px] w-[26px] flex-none rounded-[30%] bg-surface-3" />
            ) : null}

            <div className="min-w-0 flex-1">
              <p className="m-0 flex items-baseline gap-1.5">
                {line.who ? (
                  /* UserName opens the card itself and is a span on purpose -
                     a button inside a button is invalid and behaves
                     unpredictably, which is why wrapping it did nothing. */
                  <UserName
                    user={line.who}
                    profile={line.mine || real}
                    className="min-w-0 truncate text-[12.5px] font-bold"
                  />
                ) : (
                  <span className="text-[12.5px] font-bold text-ink">You</span>
                )}
                <span className="flex-none text-[10.5px] text-ghost">11:4{i}</span>
              </p>
              <p
                className={cn('m-0 min-w-0 break-words text-dim', line.big && 'leading-[1.2]')}
                style={line.big ? { fontSize: scale + 'px' } : undefined}
              >
                <EmojiText text={line.body} size={line.big ? scale : undefined} />
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* The one setting you can only judge by looking at it, put where you
          are looking. The rest stay on the page; this one is here because
          an emoji-only line is the thing it changes. */}
      {onEmojiScale ? (
        <label className="flex items-center gap-2 border-t border-line-soft bg-surface-1 px-3 py-2">
          <span className="flex-none text-[11px] font-semibold text-mute">Icon size</span>
          <input
            type="range"
            min={20}
            max={64}
            step={1}
            value={scale}
            aria-label="Size of an icon-only message"
            onChange={(e) => onEmojiScale(Number(e.target.value))}
            className="h-1.5 min-w-0 flex-1 accent-[var(--accent)]"
          />
          <span className="w-[34px] flex-none text-right text-[11px] tabular-nums text-mute">{scale}px</span>
        </label>
      ) : null}
    </div>
  );
}
